import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import "./MatematronModal.css";

const VARIANTS = {
  success: { titleDefault: "Wynik zapisany", accentVar: "var(--color-accent, #FFBE46)" },
  error: { titleDefault: "Błąd zapisu", accentVar: "var(--color-danger, #ff4d4f)" },
  info: { titleDefault: "Informacja", accentVar: "var(--color-accent, #FFBE46)" },
};

function isDarkRgbString(rgb) {
  if (!rgb) return true;
  const m = String(rgb).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return true;
  const r = Number(m[1]), g = Number(m[2]), b = Number(m[3]);
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luma < 140;
}

export default function MatematronModal({
  open,
  variant = "success",
  title,
  message,
  children,

  showBadge = false,
  badgeText = "Matematron",

  // ✅ NOWE
  headerRight = null,
  hideFooter = false,

  primaryText = "OK",
  onPrimary,
  primaryDisabled = false,

  secondaryText,
  onSecondary,
  secondaryDisabled = false,

  extraActionText,
  onExtraAction,
  extraActionDisabled = false,

  onClose,
  portalTarget,
  maxWidth,
  alignTop = false,
}) {
  const [isDark, setIsDark] = useState(true);

  const v = VARIANTS[variant] || VARIANTS.success;
  const computedTitle = title || v.titleDefault;

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    try {
      const cs = getComputedStyle(document.documentElement);
      const bg = cs.getPropertyValue("--color-bg")?.trim();
      const text = cs.getPropertyValue("--color-text")?.trim();

      if (bg) setIsDark(isDarkRgbString(bg));
      else if (text) setIsDark(!isDarkRgbString(text));
      else setIsDark(window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? true);
    } catch {
      setIsDark(window.matchMedia?.("(prefers-color-scheme: dark)")?.matches ?? true);
    }

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const styles = useMemo(() => {
    const accent = v.accentVar;

    const overlay = {
      position: "fixed",
      inset: 0,
      zIndex: 20000,
      background: isDark ? "rgba(0,0,0,.45)" : "rgba(0,0,0,.28)",
      backdropFilter: "blur(6px)",
      display: "grid",
      alignItems: alignTop ? "start" : "center",
      justifyItems: "center",
      padding: 16,
      paddingTop: alignTop ? 24 : 16,
    };

    const cardBg = isDark
      ? "linear-gradient(180deg, rgba(16,16,28,.98) 0%, rgba(10,10,18,.98) 100%)"
      : "linear-gradient(180deg, rgba(236,244,255,.96) 0%, rgba(219,234,255,.96) 100%)";

    const cardText = isDark ? "var(--color-text, #fff)" : "rgba(10,16,28,.92)";

    const card = {
      width: "100%",
      maxWidth: maxWidth || "min(680px, calc(100vw - 32px))",
      boxSizing: "border-box",
      margin: "0 auto",
      maxHeight: alignTop ? "calc(100vh - 40px)" : "calc(100vh - 32px)",

      borderRadius: 18,
      border: `2px solid ${accent}`,
      background: cardBg,
      color: cardText,
      boxShadow: "0 24px 80px rgba(0,0,0,.35), 0 10px 26px rgba(0,0,0,.20)",

      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    };

    const header = {
      padding: "16px 18px",
      borderBottom: isDark ? "1px solid rgba(255,190,70,.18)" : "1px solid rgba(0,0,0,.08)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      flex: "0 0 auto",
    };

    const titleRow = {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
      flex: "1 1 auto",
    };

    const dot = {
      width: 10,
      height: 10,
      borderRadius: 999,
      background: accent,
      boxShadow: isDark ? "0 0 10px rgba(255,153,0,.25)" : "0 0 8px rgba(255,153,0,.20)",
      flex: "0 0 auto",
    };

    const badge = {
      fontWeight: 800,
      fontSize: 13,
      padding: "6px 10px",
      borderRadius: 999,
      background: isDark ? "rgba(255,153,0,.12)" : "rgba(255,153,0,.14)",
      border: isDark ? "1px solid rgba(255,153,0,.18)" : "1px solid rgba(255,153,0,.24)",
      color: isDark ? "var(--color-accent, #FFBE46)" : "rgba(120,70,0,.92)",
      whiteSpace: "nowrap",
    };

    const headerRightWrap = {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flex: "0 0 auto",
      justifyContent: "space-between",
      minWidth: 0,
      marginLeft: "auto",
    };

    const body = {
      padding: "16px 18px",
      lineHeight: 1.55,
      opacity: 0.98,
      color: cardText,
      flex: "1 1 auto",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
    };

    const footer = {
      padding: "14px 18px 18px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      flex: "0 0 auto",
      borderTop: isDark ? "1px solid rgba(255,190,70,.12)" : "1px solid rgba(0,0,0,.06)",
      background: "rgba(0,0,0,0)",
    };

    return { overlay, card, header, titleRow, dot, badge, headerRightWrap, body, footer };
  }, [alignTop, isDark, maxWidth, v.accentVar]);

  if (!open) return null;

  const content = children ?? <div style={{ fontSize: 15 }}>{message}</div>;

  const hasFooterButtons =
    (!!secondaryText && !!onSecondary) ||
    (!!extraActionText && !!onExtraAction) ||
    (!!primaryText && !!onPrimary);

  return createPortal(
    <div
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.titleRow}>
            <div style={styles.dot} />
            <div style={{ fontSize: 18, fontWeight: 950, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {computedTitle}
            </div>
          </div>

          <div style={styles.headerRightWrap}>
            {showBadge ? <div style={styles.badge}>{badgeText}</div> : null}
            {headerRight}
          </div>
        </div>

        <div style={styles.body}>{content}</div>

        {!hideFooter && hasFooterButtons ? (
              <div style={styles.footer}>
                <div style={{ display: "flex", gap: 12, flex: "0 0 auto" }}>
                  {secondaryText && onSecondary && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onSecondary}
                      disabled={secondaryDisabled}
                    >
                      {secondaryText}
                    </button>
                  )}
                </div>

                <div
                  className="matematron-modal-actions"
                  style={{
                    display: "flex",
                    gap: 12,
                    flex: "1 1 280px",
                    minWidth: 0,
                    maxWidth: "100%",
                    justifyContent: "flex-end",
                    flexWrap: "wrap",
                  }}
                >
                  {extraActionText && onExtraAction && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={onExtraAction}
                      disabled={extraActionDisabled}
                      style={{ maxWidth: "100%", whiteSpace: "normal" }}
                    >
                      {extraActionText}
                    </button>
                  )}
                  {primaryText && onPrimary && (
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={onPrimary}
                      disabled={primaryDisabled}
                      aria-busy={primaryDisabled}
                      style={{ padding: "10px 24px", fontSize: 16, maxWidth: "100%", whiteSpace: "normal" }}
                    >
                      {primaryDisabled ? <span className="button-busy-spinner" aria-hidden="true" /> : null}
                      {primaryText}
                    </button>
                  )}
                </div>
              </div>
        ) : null}
      </div>
    </div>,
    portalTarget || document.body
  );
}
